import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase-init';
import { getEstimate, getEstimateByContractNumber } from '../../services/estimate-service';
import { getJobOrderByContractNumber } from '../../services/job-order-service';
import { generateQuotePDFBlob } from '../../services/estimate-pdf';
import { generateJobOrderPDF } from '../../services/job-order-pdf';
import { fillContractPDF, buildFieldsFromContract } from '../../services/contract-pdf-fill';
import {
  ClipboardList, Receipt, ClipboardCheck, FileSignature, Wrench, LayoutGrid, Frame,
  CalendarClock, CalendarPlus, Printer, Maximize2, FileX, Loader2, AlertTriangle,
} from 'lucide-react';
import Modal from '../ui/Modal';
import JobActions from './JobActions';
import CustomerSnapshot from './CustomerSnapshot';
import WorkDetails from './WorkDetails';
import SchedulingInfo from './SchedulingInfo';
import StatusHistory from './StatusHistory';
import MoveJobDialog from './MoveJobDialog';
import ExtendJobDialog from './ExtendJobDialog';

// Warm palette — same family used across JobApprovalModal / Estimates flow.
const WALNUT = '#8B4513';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const DOC_TABS = [
  { key: 'cutlist',    label: 'Cut List',    Icon: ClipboardList },
  { key: 'estimate',   label: 'Estimate',    Icon: Receipt },
  { key: 'contract',   label: 'Contract',    Icon: FileSignature },
  { key: 'joborder',   label: 'Job Order',   Icon: ClipboardCheck },
  { key: 'shoporder',  label: 'Shop Order',  Icon: Wrench },
  { key: 'glassorder', label: 'Glass Order', Icon: LayoutGrid },
  { key: 'flexscreen', label: 'Flex Screen', Icon: Frame },
];

function statusColor(s) {
  if (s === 'awaiting_production') return { bg: 'var(--status-soft-amber-bg)', text: 'var(--status-soft-amber-text)', dot: 'var(--status-soft-amber-dot)' };
  if (s === 'in_production')       return { bg: 'var(--status-soft-blue-bg)', text: 'var(--status-soft-blue-text)', dot: 'var(--status-soft-blue-dot)' };
  if (s === 'ready_to_install')    return { bg: 'var(--status-soft-green-bg)', text: 'var(--status-soft-green-text)', dot: 'var(--status-soft-green-dot)' };
  if (s === 'installed')           return { bg: 'var(--status-soft-purple-bg)', text: 'var(--status-soft-purple-text)', dot: 'var(--status-soft-purple-dot)' };
  return { bg: 'var(--status-soft-brown-bg)', text: WALNUT, dot: 'var(--status-soft-brown-dot)' };
}

function fmtDate(d) {
  if (!d) return null;
  try {
    const dt = d?.toDate ? d.toDate() : (d instanceof Date ? d : new Date(d));
    if (isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
}

function pdfUrl(url) {
  // Append parameters to hide sidebar and fit page width. blob: object URLs
  // (used for on-demand-generated PDFs below) don't tolerate a ?query being
  // inserted before the #fragment — the lookup breaks and the browser's PDF
  // viewer reports the file as missing. Only real Storage URLs get the query.
  if (!url) return url;
  if (url.startsWith('blob:')) {
    return url + '#navpanes=0&view=FitH&toolbar=1';
  }
  const sep = url.includes('?') ? '&' : '?';
  return url + sep + 'embedded=true#navpanes=0&view=FitH&toolbar=1';
}

/**
 * Stored/generated PDFs for the cutlist-family docs (Cut List, Shop Order,
 * Glass Order, Flex Screen) plus the cutlist-stage Estimate. Returns two
 * maps:
 *  - cutlistMap: ONLY the `pdfUrls` map pdf-storage.js writes onto the
 *    cutlistJobs doc via savePDFUrlToJob — this is the one reliable signal
 *    that a genuine cutlist-stage Estimate PDF exists.
 *  - merged: cutlistMap plus a Storage listAll fallback at
 *    jobDocuments/{year}/{contractNumber} for anything the map is missing.
 *    This is ambiguous for 'estimate' specifically — both the cutlist-stage
 *    generator AND the live quote-regeneration below upload to this same
 *    filename, so a hit here doesn't prove which one produced it. Used for
 *    the cutlist-family docs (no such ambiguity) and as a last-resort Job
 *    Order fallback only.
 */
const CUTLIST_DOC_KEYS = ['cutlist', 'shoporder', 'glassorder', 'flexscreen'];

async function fetchStoredDocuments(job) {
  let cutlistMap = {};

  if (job?.cutlistJobId) {
    try {
      const snap = await getDoc(doc(db, 'cutlistJobs', job.cutlistJobId));
      if (snap.exists()) cutlistMap = { ...(snap.data().pdfUrls || {}) };
    } catch { /* fall back to storage below */ }
  }

  const merged = { ...cutlistMap };
  const contractNumber = job?.contractNumber;
  // Skip the Storage scan entirely once the cutlist map already accounts for
  // every cutlist-family doc — nothing new to find, and listAll + a batch of
  // getDownloadURL calls isn't free at scale.
  const cutlistComplete = CUTLIST_DOC_KEYS.every(k => merged[k]);
  if (contractNumber && !cutlistComplete) {
    const folder = String(contractNumber).trim().replace(/\s+/g, '_');
    const thisYear = new Date().getFullYear();
    for (const year of [thisYear, thisYear - 1, thisYear - 2]) {
      try {
        const { items } = await listAll(ref(storage, `jobDocuments/${year}/${folder}`));
        if (items.length === 0) continue;
        const found = await Promise.all(items.map(async (item) => {
          const key = item.name.replace(/\.pdf$/i, '');
          if (merged[key]) return null;
          return [key, await getDownloadURL(item)];
        }));
        found.filter(Boolean).forEach(([k, u]) => { merged[k] = u; });
        break;
      } catch { /* try previous year */ }
    }
  }

  return { cutlistMap, merged };
}

// Session-local cache so repeatedly opening/closing the same job's popup
// doesn't re-run the same Firestore/Storage lookups every time — keeps
// Estimate/Job Order/stored-doc resolution to one round trip per job per
// visit to this page, not one per click. Short TTL keeps "always regenerate
// live" honest for anyone who leaves the tab open a long time.
const _docLookupCache = new Map(); // key -> { data, ts }
const DOC_LOOKUP_TTL_MS = 3 * 60 * 1000;

async function resolveJobDocuments(job) {
  const cacheKey = `${job?.cutlistJobId || ''}|${job?.contractNumber || ''}`;
  const cached = _docLookupCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < DOC_LOOKUP_TTL_MS) return cached.data;

  const [{ cutlistMap, merged }, jobOrder] = await Promise.all([
    fetchStoredDocuments(job),
    getJobOrderByContractNumber(job?.contractNumber).catch(() => null),
  ]);

  let estimate = jobOrder?.estimateId
    ? await getEstimate(jobOrder.estimateId).catch(() => null)
    : null;
  if (!estimate) {
    estimate = await getEstimateByContractNumber(job?.contractNumber).catch(() => null);
  }

  const data = { cutlistMap, merged, jobOrder, estimate };
  _docLookupCache.set(cacheKey, { data, ts: Date.now() });
  return data;
}

export default function JobDetailView({
  job, isOpen, onClose, onEdit, onSchedule, onComplete, onCancel, onDelete, onStatusChange
}) {
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [pdfUrls, setPdfUrls] = useState({});
  const [cutlistPdfUrls, setCutlistPdfUrls] = useState({});
  const [estimateDoc, setEstimateDoc] = useState(null);
  const [jobOrderDoc, setJobOrderDoc] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [activeDocTab, setActiveDocTab] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(null);
  const [docRawSrc, setDocRawSrc] = useState(null);
  const objectUrlRef = useRef(null);

  // Every job in Production started as an Estimate → Job Order. There's no
  // direct estimateId stored on the job — contractNumber is the only thread
  // that ties jobs → job orders → estimates together, so that's the lookup.
  useEffect(() => {
    setPdfUrls({});
    setCutlistPdfUrls({});
    setEstimateDoc(null);
    setJobOrderDoc(null);
    setActiveDocTab(null);
    let cancelled = false;

    if (job?.cutlistJobId || job?.contractNumber) {
      setDocsLoading(true);
      // Job Order's contractNumber is always correctly stamped (job-order-service.js
      // guarantees it). Find the Job Order first, then get its Estimate by doc ID —
      // this sidesteps historical records where the Estimate's own contractNumber
      // field was never correctly written (a since-fixed bug in CreateJobOrderPage).
      resolveJobDocuments(job)
        .then(({ cutlistMap, merged, jobOrder, estimate }) => {
          if (cancelled) return;
          setPdfUrls(merged);
          setCutlistPdfUrls(cutlistMap);
          setEstimateDoc(estimate);
          setJobOrderDoc(jobOrder);

          const source = (key) => docSourceFor(key, merged, cutlistMap, estimate, jobOrder);
          const first = DOC_TABS.find(t => source(t.key));
          if (first) setActiveDocTab(first.key);
        })
        .finally(() => { if (!cancelled) setDocsLoading(false); });
    }

    return () => { cancelled = true; };
  }, [job?.cutlistJobId, job?.contractNumber]);

  // Revoke any generated-PDF object URL when we move off it or unmount.
  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  // Resolve whichever tab is active into an actual URL — either the stored
  // one, or a freshly generated blob for Estimate / Contract / Job Order.
  useEffect(() => {
    if (!activeDocTab || docsLoading) return;
    const source = docSourceFor(activeDocTab, pdfUrls, cutlistPdfUrls, estimateDoc, jobOrderDoc);
    if (!source) return;

    let cancelled = false;
    setDocLoading(true);
    setDocError(null);
    setDocRawSrc(null);

    (async () => {
      try {
        if (source.type === 'stored') {
          if (!cancelled) setDocRawSrc(source.url);
          return;
        }

        let blob;
        if (source.type === 'generate-estimate') {
          blob = await generateQuotePDFBlob(estimateDoc, {}, jobOrderDoc?.contractNumber || job?.contractNumber || null);
        } else if (source.type === 'generate-contract') {
          const contract = estimateDoc.contract;
          const isSigned = contract.contractStatus === 'signed';
          const fields = buildFieldsFromContract(contract);
          const bytes = await fillContractPDF(fields, contract.repSignature || null, isSigned ? contract.signedBy?.signatureData : null);
          blob = new Blob([bytes], { type: 'application/pdf' });
        } else if (source.type === 'generate-joborder') {
          blob = await generateJobOrderPDF(jobOrderDoc, true);
        }

        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = objUrl;
        setDocRawSrc(objUrl);
      } catch (e) {
        console.error('Document generation failed:', e);
        if (!cancelled) { setDocError("Couldn't generate this document."); setDocLoading(false); }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocTab, pdfUrls, cutlistPdfUrls, estimateDoc, jobOrderDoc, docsLoading]);

  if (!job) return null;

  const availablePDFs = DOC_TABS.filter(t => docSourceFor(t.key, pdfUrls, cutlistPdfUrls, estimateDoc, jobOrderDoc));
  const missingPDFs = DOC_TABS.filter(t => !docSourceFor(t.key, pdfUrls, cutlistPdfUrls, estimateDoc, jobOrderDoc));
  const hasPDFs = availablePDFs.length > 0;
  const showDocPanel = !!(job.cutlistJobId || job.contractNumber);
  const sc = statusColor(job.status);

  function handleScheduleClick() {
    if (job.scheduledDate) setShowMoveDialog(true);
    else onSchedule(job);
  }

  return (
    <Modal maxWidth={showDocPanel ? "96vw" : "660px"} isOpen={isOpen} onClose={onClose} title="" showTitle={false}>
      <div style={{ display: "flex", height: "90vh", overflow: "hidden", fontFamily: FONT_SANS }}>

        {/* ━━━ LEFT PANEL ━━━ */}
        <div style={{
          width: showDocPanel ? "35%" : "100%", flexShrink: 0,
          display: "flex", flexDirection: "column",
          background: "var(--jobdetail-left-bg)",
          borderRight: "1px solid var(--jobdetail-left-border)"
        }}>

          {/* Job identity */}
          <div style={{ padding: "24px 24px 20px", borderBottom: "1px solid var(--jobdetail-left-divider)" }}>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--jobdetail-eyebrow)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "6px" }}>
              Work Order
            </div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: "28px", fontWeight: "600", color: "var(--jobdetail-customer-name)", letterSpacing: "-0.5px", lineHeight: 1, marginBottom: "8px" }}>
              #{job.woNumber || job.contractNumber || "—"}
            </div>
            <div style={{ fontSize: "17px", fontWeight: "600", color: "var(--jobdetail-customer-detail)", marginBottom: "12px" }}>
              {job.customerName || "—"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 12px", borderRadius: "20px", background: sc.bg, color: sc.text, fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: sc.dot, flexShrink: 0, display: "inline-block" }} />
                {(job.status || "").replace(/_/g, " ")}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--jobdetail-left-divider)", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <JobActions job={job}
              onEdit={() => onEdit(job)}
              onSchedule={handleScheduleClick}
              onComplete={() => onComplete(job)}
              onCancel={() => onCancel(job)}
              onDelete={() => onDelete(job)}
              onStatusChange={(s) => onStatusChange(job, s)}
            />
            {job.scheduledDate && <>
              <button onClick={() => setShowMoveDialog(true)}
                style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", background: "var(--jobdetail-chip-bg)", border: "1px solid var(--jobdetail-chip-border)", borderRadius: "8px", color: "var(--jobdetail-chip-text)", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}>
                <CalendarClock size={13} strokeWidth={2.25} /> Move
              </button>
              <button onClick={() => setShowExtendDialog(true)}
                style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", background: "var(--jobdetail-chip-bg)", border: "1px solid var(--jobdetail-chip-border)", borderRadius: "8px", color: "var(--jobdetail-chip-text)", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}>
                <CalendarPlus size={13} strokeWidth={2.25} /> Extend
              </button>
            </>}
          </div>

          {/* Scrollable details */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

            {/* Key facts grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "24px" }}>
              {[
                { label: "Job Type", value: job.jobType },
                { label: "Sold Date", value: fmtDate(job.soldDate) },
                { label: "Windows", value: job.windowCount > 0 ? `${job.windowCount} window${job.windowCount !== 1 ? "s" : ""}` : null },
                { label: "Doors", value: job.doorCount > 0 ? `${job.doorCount} door${job.doorCount !== 1 ? "s" : ""}` : null },
              ].filter(r => r.value).map(r => (
                <div key={r.label} style={{ padding: "12px 14px", background: "var(--jobdetail-section-bg)", borderRadius: "10px", border: "1px solid var(--jobdetail-section-border)" }}>
                  <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", color: "var(--jobdetail-label-muted)", marginBottom: "4px" }}>{r.label}</div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "var(--jobdetail-customer-name)", lineHeight: 1.3 }}>{r.value}</div>
                </div>
              ))}
            </div>

            {/* Divider label */}
            <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1.5px", color: "var(--jobdetail-label-muted)", marginBottom: "14px" }}>
              Customer Info
            </div>
            <div style={{ marginBottom: "24px" }}>
              <CustomerSnapshot job={job} />
            </div>

            <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1.5px", color: "var(--jobdetail-label-muted)", marginBottom: "14px" }}>
              Scheduling
            </div>
            <div style={{ marginBottom: "24px" }}>
              <SchedulingInfo job={job} />
            </div>

            <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1.5px", color: "var(--jobdetail-label-muted)", marginBottom: "14px" }}>
              Work Details
            </div>
            <div style={{ marginBottom: "24px" }}>
              <WorkDetails job={job} />
            </div>

            <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1.5px", color: "var(--jobdetail-label-muted)", marginBottom: "14px" }}>
              Status History
            </div>
            <StatusHistory statusHistory={job.statusHistory} />
          </div>
        </div>

        {/* ━━━ RIGHT PANEL: DOCUMENTS ━━━ */}
        {showDocPanel && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#1a1a1a", minWidth: 0 }}>

            {/* Tab bar */}
            <div style={{ display: "flex", alignItems: "center", background: "#111", flexShrink: 0, borderBottom: "1px solid #2a2a2a" }}>
              <div style={{ display: "flex", flex: 1, overflowX: "auto" }}>
                {availablePDFs.map(tab => {
                  const active = activeDocTab === tab.key;
                  return (
                    <button key={tab.key}
                      onClick={() => setActiveDocTab(tab.key)}
                      style={{
                        display: "flex", alignItems: "center", gap: "7px",
                        padding: "13px 18px", border: "none", cursor: "pointer", fontFamily: FONT_SANS,
                        fontSize: "13px", fontWeight: active ? "700" : "500", whiteSpace: "nowrap",
                        background: active ? "#1a1a1a" : "transparent",
                        color: active ? "#fff" : "#8a8a8a",
                        borderBottom: active ? `3px solid ${WALNUT}` : "3px solid transparent",
                        transition: "all 0.15s"
                      }}>
                      <tab.Icon size={14} strokeWidth={2.25} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {hasPDFs && docRawSrc && (
                <div style={{ display: "flex", gap: "8px", padding: "0 56px 0 16px", flexShrink: 0 }}>
                  <button onClick={() => window.open(docRawSrc, "_blank")}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", borderRadius: "7px", border: "1px solid #333", background: "#222", color: "#aaa", cursor: "pointer", fontSize: "12px", fontFamily: FONT_SANS, fontWeight: "600" }}>
                    <Printer size={13} strokeWidth={2.25} /> Print
                  </button>
                  <a href={docRawSrc} target="_blank" rel="noreferrer"
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", borderRadius: "7px", background: WALNUT, color: "white", fontSize: "12px", fontWeight: "600", textDecoration: "none" }}>
                    <Maximize2 size={13} strokeWidth={2.25} /> Full Screen
                  </a>
                </div>
              )}
            </div>

            {/* Missing-docs note */}
            {!docsLoading && missingPDFs.length > 0 && missingPDFs.length < DOC_TABS.length && (
              <div style={{ padding: "8px 18px", background: "#151515", borderBottom: "1px solid #262626", fontSize: "11px", color: "#6b6b6b", fontFamily: FONT_SANS }}>
                Not yet generated: {missingPDFs.map(t => t.label).join(', ')}
              </div>
            )}

            {/* Document area */}
            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
              {docsLoading && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", justifyContent: "center", background: "#1a1a1a", zIndex: 2, color: "#555", fontFamily: FONT_SANS }}>
                  <Loader2 size={20} strokeWidth={2} style={{ animation: "twpDocSpin 0.9s linear infinite" }} />
                  <span style={{ fontSize: "13px" }}>Checking for documents…</span>
                </div>
              )}

              {!docsLoading && !hasPDFs && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", justifyContent: "center", color: "#555", fontFamily: FONT_SANS, padding: "0 40px", textAlign: "center" }}>
                  <FileX size={28} strokeWidth={1.75} />
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#888" }}>No documents yet</div>
                  <div style={{ fontSize: "12.5px", lineHeight: 1.5, maxWidth: "320px" }}>
                    Cut list, shop order, glass order, estimate, contract and job order PDFs will show up here as soon as they&apos;re available.
                  </div>
                </div>
              )}

              {!docsLoading && hasPDFs && docError && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", justifyContent: "center", color: "#888", fontFamily: FONT_SANS, padding: "0 40px", textAlign: "center" }}>
                  <AlertTriangle size={26} strokeWidth={1.75} color="#c17a5a" />
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#c17a5a" }}>{docError}</div>
                </div>
              )}

              {!docsLoading && hasPDFs && !docError && (
                <>
                  {docLoading && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#1a1a1a", zIndex: 2 }}>
                      <div style={{ color: "#444", fontSize: "14px", letterSpacing: "1px" }}>Loading document…</div>
                    </div>
                  )}
                  {docRawSrc && (
                    <iframe
                      key={activeDocTab}
                      src={pdfUrl(docRawSrc)}
                      onLoad={() => setDocLoading(false)}
                      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                      title={activeDocTab}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes twpDocSpin { to { transform: rotate(360deg); } }`}</style>

      <MoveJobDialog isOpen={showMoveDialog} onClose={() => setShowMoveDialog(false)} job={job} />
      <ExtendJobDialog isOpen={showExtendDialog} onClose={() => setShowExtendDialog(false)} job={job} />
    </Modal>
  );
}

/**
 * Where a given doc tab's PDF actually comes from, given what we've loaded so far.
 * - Cut List / Shop Order / Glass Order / Flex Screen: stored file only (cutlist pipeline).
 * - Estimate: the stored cutlist-stage PDF if the job has reached cutlist; otherwise
 *   regenerated live from the originating /estimates quote (found via contractNumber).
 *   Uses `cutlistPdfUrls` (the raw cutlistJobs.pdfUrls map) for that check, NOT the
 *   merged/Storage-fallback map — both the cutlist-stage generator and this file's own
 *   live regeneration upload to the identical Storage filename, so a hit in the merged
 *   map doesn't prove a genuine cutlist-stage PDF exists; only the Firestore map field
 *   (written exclusively by the cutlist-stage generator) does.
 * - Contract: always regenerated live from the estimate's embedded contract data —
 *   there's no reliable stored-file reference for this one.
 * - Job Order: always regenerated live from the linked Job Order doc; falls back to a
 *   manually-uploaded stored copy only if no Job Order doc can be found at all.
 */
function docSourceFor(key, pdfUrls, cutlistPdfUrls, estimateDoc, jobOrderDoc) {
  if (key === 'cutlist' || key === 'shoporder' || key === 'glassorder' || key === 'flexscreen') {
    return pdfUrls[key] ? { type: 'stored', url: pdfUrls[key] } : null;
  }
  if (key === 'estimate') {
    if (cutlistPdfUrls.estimate) return { type: 'stored', url: cutlistPdfUrls.estimate };
    return estimateDoc ? { type: 'generate-estimate' } : null;
  }
  if (key === 'contract') {
    return estimateDoc?.contract?.contractNumber ? { type: 'generate-contract' } : null;
  }
  if (key === 'joborder') {
    if (jobOrderDoc) return { type: 'generate-joborder' };
    return pdfUrls.joborder ? { type: 'stored', url: pdfUrls.joborder } : null;
  }
  return null;
}
